import React, { type KeyboardEvent, type MouseEvent, useCallback, useState, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
	type OnOpenChangeArgs,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Lozenge from '@atlaskit/lozenge';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';

import { THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX } from '../../../../../common/constants';
import type { SupportedThirdParty } from '../../../../../common/constants/import-type';
import type { ThirdPartyFieldMapping } from '../../../../../common/types';
import { getIcon } from '../../../../../common/utils/fields';
import { useThirdPartyFieldConfigState } from '../../../../../controllers/import-state';
import { ExistingFieldsSection } from './existing-fields-section';
import messages from './messages';
import { NewCustomFieldsSection } from './new-custom-fields-section';

type Props = {
	thirdParty: SupportedThirdParty;
	thirdPartyFieldMapping: ThirdPartyFieldMapping;
};

export const FieldSelector = ({ thirdParty, thirdPartyFieldMapping }: Props) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [isCustomFieldsMenuOpen, setIsCustomFieldsMenuOpen] = useState(false);
	const { mappedJiraField, mappingContext, thirdPartyField, compatibleExistingFieldTypes } =
		thirdPartyFieldMapping;
	const [thirdPartyFieldConfigState] = useThirdPartyFieldConfigState();

	const toggleMenuOpenState = useCallback((attrs: OnOpenChangeArgs) => {
		setIsOpen(attrs.isOpen);

		if (!attrs.isOpen) {
			setIsCustomFieldsMenuOpen(false);
		}
	}, []);

	const onClickSelectCustomField = useCallback(
		(event: MouseEvent<Element> | KeyboardEvent<Element>) => {
			event.stopPropagation();
			setIsCustomFieldsMenuOpen(true);
		},
		[],
	);

	const onClickGoBack = useCallback((event: MouseEvent<Element> | KeyboardEvent<Element>) => {
		event.stopPropagation();
		setIsCustomFieldsMenuOpen(false);
	}, []);

	const onKeyDownHandler = useCallback((event: KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Tab') {
			event.stopPropagation();
		}
	}, []);

	const getFieldLozenge = useCallback(
		(isDisabled = false) => {
			if (mappingContext.isRequired) {
				return <Lozenge appearance="default">{formatMessage(messages.required)}</Lozenge>;
			}

			if (!mappedJiraField.fieldId) {
				return (
					<Lozenge appearance={isDisabled ? 'default' : 'new'}>
						{formatMessage(messages.newField)}
					</Lozenge>
				);
			}

			return null;
		},
		[formatMessage, mappedJiraField.fieldId, mappingContext.isRequired],
	);

	// disable the field if only one field option is available
	const shouldDisableField = useMemo(() => {
		if (thirdPartyFieldConfigState === null) return false;
		const { availableJiraFields } = thirdPartyFieldConfigState;
		const availableJiraFieldItems = compatibleExistingFieldTypes.flatMap(
			(fieldType) => availableJiraFields[fieldType] ?? [],
		);

		return (
			(availableJiraFieldItems?.length || 0) +
				thirdPartyFieldMapping.compatibleNewFieldTypes.length <=
			1
		);
	}, [
		compatibleExistingFieldTypes,
		thirdPartyFieldConfigState,
		thirdPartyFieldMapping.compatibleNewFieldTypes.length,
	]);

	const dropdownTrigger = useCallback(
		({ triggerRef, isSelected, ...props }: CustomTriggerProps<HTMLElement>) => {
			const isDisabled = !mappingContext.isIncluded;
			return (
				<StyledButton
					{...props}
					iconAfter={<ChevronDownIcon label="" size="large" />}
					ref={triggerRef}
					isOpen={isSelected}
					isDisabled={isDisabled}
				>
					<ContentLeftWrapper>
						{getIcon({ fieldType: mappedJiraField.fieldType, isDisabled })}
						<Header>
							<Tooltip content={mappedJiraField.fieldName} position="bottom-start">
								<span>{mappedJiraField.fieldName}</span>
							</Tooltip>
						</Header>
					</ContentLeftWrapper>
					<ContentRightWrapper>{getFieldLozenge(isDisabled)}</ContentRightWrapper>
				</StyledButton>
			);
		},
		[
			getFieldLozenge,
			mappedJiraField.fieldName,
			mappedJiraField.fieldType,
			mappingContext.isIncluded,
		],
	);

	const fixedFieldContent = useMemo(() => {
		const isDisabled = !mappingContext.isIncluded;
		const showDropdownIcon = !mappingContext.isRequired;
		return (
			<FixedFieldWrapper isDisabled={isDisabled} showDropdownIcon={showDropdownIcon}>
				<ContentLeftWrapper>
					{getIcon({
						fieldType: mappedJiraField.fieldType,
						isDisabled,
						isSubtle: !isDisabled,
					})}
					<Header>{mappedJiraField.fieldName}</Header>
				</ContentLeftWrapper>
				<ContentRightWrapper>{getFieldLozenge(isDisabled)}</ContentRightWrapper>
				{showDropdownIcon && (
					<ContentIconWrapper>
						<ChevronDownIcon
							label=""
							size="large"
							testId="project-create-importer-upload-flow.ui.third-party-review-screen.fields-mapping.fields-mapping-content.field-selector.chevron-down-icon"
						/>
					</ContentIconWrapper>
				)}
			</FixedFieldWrapper>
		);
	}, [
		getFieldLozenge,
		mappedJiraField.fieldName,
		mappedJiraField.fieldType,
		mappingContext.isIncluded,
		mappingContext.isRequired,
	]);

	if (mappingContext.isRequired) {
		return (
			<Tooltip content={formatMessage(messages.requiredFieldToolTip)}>{fixedFieldContent}</Tooltip>
		);
	}

	if (mappingContext.isFieldDisabled || shouldDisableField) {
		return fixedFieldContent;
	}

	return (
		<Wrapper data-testid={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.dropdown-menu`}>
			<DropdownMenu
				testId={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.dropdown-menu-${thirdPartyField.fieldName}`}
				isOpen={isOpen}
				onOpenChange={toggleMenuOpenState}
				shouldFlip
				trigger={dropdownTrigger}
			>
				<DropdownItemWrapper onKeyDown={onKeyDownHandler}>
					{isCustomFieldsMenuOpen ? (
						<NewCustomFieldsSection
							thirdPartyFieldMapping={thirdPartyFieldMapping}
							onClickGoBack={onClickGoBack}
						/>
					) : (
						<ExistingFieldsSection
							thirdParty={thirdParty}
							thirdPartyFieldMapping={thirdPartyFieldMapping}
							onClickSelectCustomField={onClickSelectCustomField}
						/>
					)}
				</DropdownItemWrapper>
			</DropdownMenu>
		</Wrapper>
	);
};

type FixedFieldWrapperProps = {
	isDisabled: boolean;
	showDropdownIcon: boolean;
};

export const FIELD_SELECTOR_WIDTH = 275;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FixedFieldWrapper = styled.div<FixedFieldWrapperProps>({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	boxSizing: 'border-box',
	whiteSpace: 'nowrap',
	width: `${FIELD_SELECTOR_WIDTH}px`,
	minWidth: 0,
	height: token('space.400', '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingRight: `${({ showDropdownIcon }: FixedFieldWrapperProps) =>
		showDropdownIcon ? 0 : token('space.150', '12px')}`,
	paddingLeft: token('space.050', '4px'),
	borderRadius: '4px',
	cursor: 'not-allowed',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: `${({ isDisabled }: FixedFieldWrapperProps) =>
		isDisabled
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.disabled', colors.N400)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.subtlest', colors.N700)} `,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.disabled', colors.N10),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `2px solid ${token('color.border.disabled', colors.N20)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	boxSizing: 'border-box',
	whiteSpace: 'nowrap',
	width: `${FIELD_SELECTOR_WIDTH}px`,
	minWidth: 0,
	height: token('space.400', '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		alignItems: 'center',
		padding: '0px',
		width: '100%',
		minWidth: 0,
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			display: 'flex',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.span({
	margin: `0px ${token('space.050', '4px')}`,
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentLeftWrapper = styled.div({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-start',
	marginLeft: token('space.100', '8px'),
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentIconWrapper = styled.div({
	marginRight: token('space.negative.025', '-2px'),
	marginLeft: token('space.050', '2px'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentRightWrapper = styled.div({
	display: 'flex',
	flex: '0 1 auto',
	flexDirection: 'row',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		height: token('space.300', '24px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemWrapper = styled.div({
	maxHeight: '50vh',
	width: `${FIELD_SELECTOR_WIDTH}px`,
	paddingBottom: token('space.050', '4px'),
});

type ButtonProps = {
	isOpen?: boolean;
};

// Override default AK button styling

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)<ButtonProps>({
	'&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.input', colors.N0),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		border: `2px solid ${({ isOpen }: ButtonProps) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			isOpen ? token('color.border.focused', colors.B100) : token('color.border', colors.N30)}`,
		':active': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			color: `${token('color.text', colors.N900)}`,
		},
		// this focus styling does not do anything but reconcile missing focus styles in AKButton possibly related to #hot-103234
		':focus': {
			outlineOffset: '0',
		},
	},
});
